import type { HttpClient } from "./http";

export type JobStatus = "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED" | "CANCELLED";

export interface JobDTO {
  id: string;
  label: string;
  status: JobStatus;
  /** 0–100, or -1 when the work can't report a fraction. */
  progress: number;
  message: string;
  /** Per-row problems, typically import validation failures. */
  details: string[];
  error: string | null;
  /**
   * API path the artifact can be fetched from, for jobs that generate a file.
   * Null for jobs that produce no download.
   */
  download_path: string | null;
  created_at: string;
  updated_at: string;
}

export function isFinished(status: JobStatus): boolean {
  return status === "SUCCEEDED" || status === "FAILED" || status === "CANCELLED";
}

export class JobClient {
  constructor(private readonly http: HttpClient) {}

  /** The caller's recent jobs, newest first. */
  mine(): Promise<JobDTO[]> {
    return this.http.get<JobDTO[]>("/job");
  }

  get(id: string): Promise<JobDTO> {
    return this.http.get<JobDTO>(`/job/${id}`);
  }

  /** Best effort — queued work never starts, running work stops at its next check. */
  async cancel(id: string): Promise<void> {
    await this.http.post<void>(`/job/${id}/cancel`, undefined, { responseType: "void" });
  }

  /**
   * Clears finished jobs from the list and returns how many went.
   * Running work is kept — hiding it would only cost the operator visibility.
   */
  clearFinished(): Promise<number> {
    return this.http.delete<number>("/job");
  }

  /** Dismisses one finished job. */
  async dismiss(id: string): Promise<void> {
    await this.http.delete<void>(`/job/${id}`, { responseType: "void" });
  }
}
